import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { Expense, PaymentMethod, Receipt, Category } from "@/models/types";
import { useToast } from "@/store/ToastContext";
import { validateExpense } from "@/utils/validation";
import { todayISO } from "@/utils/date";
import { PAYMENT_METHODS } from "@/utils/format";
import { expenseApi } from "@/services/api";
import AmountInput from "@/components/feature/AmountInput";
import ReceiptUploader from "@/components/feature/ReceiptUploader";
import Input from "@/components/base/Input";
import Button from "@/components/base/Button";

interface ExpenseFormProps {
  initial?: Expense | null;
  categories: Category[];
  onSaved?: () => void;
}
export default function ExpenseForm({ initial, categories }: ExpenseFormProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [date, setDate] = useState(initial?.date ?? todayISO());
  const [description, setDescription] = useState(initial?.description ?? "");
  const [categoryId, setCategoryId] = useState(initial?.categoryId ?? "");
  const [amount, setAmount] = useState(
    initial ? String(initial.amount) : "",
  );
  const [paymentMethod, setPaymentMethod] = useState<string>(
    initial?.paymentMethod ?? "tarjeta",
  );
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [receipt, setReceipt] = useState<Receipt | null>(
    initial?.receipt ?? null,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const draft = {
      date,
      description,
      categoryId,
      amount,
      paymentMethod,
      notes,
    };
    const { valid, errors: errs } = validateExpense(draft);
    setErrors(errs);
    if (!valid) return;

    setSaving(true);
    const amountNum = parseFloat(amount);
    const expenseData = {
      date: new Date(date).toISOString(),
      description,
      category_id: categoryId,   // Nota: el backend espera category_id
      amount: amountNum,
      payment_method: paymentMethod as PaymentMethod,
      notes,
      receipt: receipt ?? undefined,
    };

    try {
      if (initial) {
        // Actualizar gasto existente
        const updated = await expenseApi.update(initial.id, expenseData);
        showToast("Gasto actualizado correctamente.");
      } else {
        // Crear nuevo gasto
        const created = await expenseApi.create(expenseData);
        showToast("Gasto guardado correctamente.");
      }
      navigate("/gastos");
    } catch (error) {
      console.error("Error guardando gasto:", error);
      showToast("Error al guardar el gasto.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Importe destacado */}
      <div>
        <label className="block text-sm font-medium text-foreground-800 mb-1.5">
          Importe
        </label>
        <AmountInput
          value={amount}
          onChange={setAmount}
          error={errors.amount}
          autoFocus={!initial}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          type="date"
          label="Fecha del gasto"
          name="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          error={errors.date}
        />
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1.5">
            Método de pago
          </label>
          <div className="grid grid-cols-2 gap-2">
            {PAYMENT_METHODS.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setPaymentMethod(m.value)}
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  paymentMethod === m.value
                    ? "border-primary-400 bg-primary-100 text-primary-700"
                    : "border-secondary-300 text-foreground-700 hover:bg-background-100"
                }`}
              >
                <i className={`${m.icon} text-base`} />
                {m.label}
              </button>
            ))}
          </div>
          {errors.paymentMethod && (
            <p className="mt-1 text-xs text-red-500">{errors.paymentMethod}</p>
          )}
        </div>
      </div>

      <Input
        label="Descripción"
        name="description"
        placeholder="Ej. Supermercado, Uber, Netflix..."
        icon="ri-edit-line"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        error={errors.description}
      />

      {/* Categoría */}
      <div>
        <label className="block text-sm font-medium text-foreground-800 mb-1.5">
          Categoría
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setCategoryId(cat.id)}
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                categoryId === cat.id
                  ? "border-primary-400 bg-primary-100 text-primary-700"
                  : "border-secondary-300 text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i className={cat.icon} style={{ color: cat.color }} />
              {cat.name}
            </button>
          ))}
        </div>
        {errors.categoryId && (
          <p className="mt-1 text-xs text-red-500">{errors.categoryId}</p>
        )}
      </div>

      {/* Recibo */}
      <div>
        <label className="block text-sm font-medium text-foreground-800 mb-1.5">
          Recibo (opcional)
        </label>
        <ReceiptUploader value={receipt} onChange={setReceipt} />
      </div>

      {/* Notas */}
      <div>
        <label className="block text-sm font-medium text-foreground-800 mb-1.5">
          Notas (opcional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder="Agrega algún detalle adicional..."
          className="w-full rounded-md border border-secondary-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none placeholder:text-foreground-400"
        />
        <p className="mt-1 text-xs text-foreground-400 text-right">
          {notes.length}/500
        </p>
      </div>

      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={saving}
        icon={saving ? "ri-loader-4-line animate-spin" : "ri-check-line"}
      >
        {saving ? "Guardando..." : initial ? "Guardar cambios" : "Guardar gasto"}
      </Button>
    </form>
  );
}